import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Minus, Upload, Search, X, Scissors, Package, ShoppingBag, ChevronDown } from 'lucide-react';
import api from '../../../config/axios-conf';
import { getServices } from '../../../api/service.api';
import { formatCurrency } from '../../../utils/formatters';
import { useDebounce } from '../../../hooks/useDebounce';
import KeypadInput from '../../../components/common/KeypadInput';
import { useToastContext } from '../../../contexts/ToastContext';
import styles from '../../../assets/scss/pages/ServiceCreate.module.scss';

const DEFAULT_IMG = 'https://sotiem.vn/uploads/default-service.png';

function getItemPrice(variant) {
    return (variant.salePrice && Number(variant.salePrice) > 1000
        ? Number(variant.salePrice)
        : Number(variant.retailPrice)) || 0;
}

export default function ComboCreateForm() {
    const navigate = useNavigate();
    const fileRef = useRef(null);

    const [form, setForm] = useState({
        name: '',
        description: '',
        displayLocation: '',
        internalNote: '',
        isWeb: false,
        urlImg: '',
        customPrice: '',
    });
    const [nameEdited, setNameEdited] = useState(false); // user đã tự sửa tên chưa
    const [comboItems, setComboItems] = useState([]);
    const [allServices, setAllServices] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [catalogTab, setCatalogTab] = useState('all'); // all | dichvu | sanpham
    const [imgPreview, setImgPreview] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [loadingServices, setLoadingServices] = useState(true);

    const { showError } = useToastContext();
    // Báo lỗi đồng thời: banner inline (đầu form) + toast trượt góc phải.
    const reportError = (msg) => {
        setError(msg);
        showError(msg);
    };

    // Variant picker: khi service có nhiều variants
    const [variantPicker, setVariantPicker] = useState(null); // { service, variants }

    const debouncedSearch = useDebounce(searchText, 300);

    useEffect(() => {
        const fetchAll = async () => {
            setLoadingServices(true);
            try {
                const [dvRes, spRes] = await Promise.all([
                    getServices({ limit: 100, typeService: 'dichvu' }),
                    getServices({ limit: 100, typeService: 'sanpham' }),
                ]);
                const dv = (dvRes.data.services || []).filter((s) => !s.isDeleted).map((s) => ({ ...s, _type: 'dichvu' }));
                const sp = (spRes.data.services || []).filter((s) => !s.isDeleted).map((s) => ({ ...s, _type: 'sanpham' }));
                setAllServices([...dv, ...sp]);
            } catch {
                setAllServices([]);
            } finally {
                setLoadingServices(false);
            }
        };
        fetchAll();
    }, []);

    const updateForm = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const filteredServices = allServices.filter((s) => {
        if (catalogTab !== 'all' && s._type !== catalogTab) return false;
        if (!debouncedSearch.trim()) return true;
        const keyword = debouncedSearch.trim().toLowerCase().replace(/\s/g, '');
        const keywordNoDiac = keyword.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const name = (s.name || '').toLowerCase().replace(/\s/g, '');
        const nameNoDiac = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const code = (s.code || '').toLowerCase().replace(/\s/g, '');
        return nameNoDiac.includes(keywordNoDiac) || name.includes(keyword) || code.includes(keyword);
    });

    // Lấy tên hiển thị variant
    const getVariantLabel = (variant) => {
        if (variant?.attributeValues?.length > 0) {
            return variant.attributeValues.map((v) => v.value).join(' - ');
        }
        return variant?.sku || 'Mặc định';
    };

    // Auto-generate tên combo từ 2 item đầu tiên
    const autoGenerateName = (items) => {
        if (items.length === 0) return '';
        const names = items.slice(0, 2).map((item) => item.service.name);
        return names.join(' + ') + (items.length > 2 ? ` +${items.length - 2}` : '');
    };

    // Thêm item với variant cụ thể
    const addItemWithVariant = (service, variant) => {
        const exists = comboItems.find(
            (item) => item.service.id === service.id && item.variant.id === variant.id,
        );

        let newItems;
        if (exists) {
            newItems = comboItems.map((item) =>
                item.service.id === service.id && item.variant.id === variant.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item,
            );
        } else {
            newItems = [...comboItems, { service, variant, quantity: 1 }];
        }
        setComboItems(newItems);
        setVariantPicker(null);

        if (!nameEdited) {
            updateForm('name', autoGenerateName(newItems));
        }
    };

    // Click service → nếu 1 variant thêm luôn, nhiều variants toggle picker
    const addItem = (service) => {
        const variants = service.variants || [];
        if (variants.length === 0) return;
        if (variants.length === 1) {
            addItemWithVariant(service, variants[0]);
        } else {
            // Toggle: đang mở thì đóng, đang đóng thì mở
            setVariantPicker((prev) =>
                prev?.service.id === service.id ? null : { service, variants },
            );
        }
    };

    const updateQty = (idx, delta) => {
        setComboItems((prev) =>
            prev.map((item, i) => (i !== idx ? item : { ...item, quantity: Math.max(1, item.quantity + delta) })),
        );
    };

    const removeItem = (idx) => {
        const newItems = comboItems.filter((_, i) => i !== idx);
        setComboItems(newItems);
        if (!nameEdited) {
            updateForm('name', autoGenerateName(newItems));
        }
    };

    const comboCalcTotal = comboItems.reduce((sum, item) => sum + getItemPrice(item.variant) * item.quantity, 0);
    const comboTotal = form.customPrice !== '' ? Number(String(form.customPrice).replace(/\./g, '')) || 0 : comboCalcTotal;

    const handleImageSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImgPreview(URL.createObjectURL(file));
        const formData = new FormData();
        formData.append('file', file);
        try {
            const { data } = await api.post('/upload/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 20000,
            });
            updateForm('urlImg', data.url);
        } catch {
            reportError('Upload ảnh thất bại');
        }
    };

    const handleSubmit = async () => {
        setError('');
        if (!form.name.trim()) { reportError('Vui lòng nhập tên combo'); return; }
        if (comboItems.length === 0) { reportError('Vui lòng thêm ít nhất 1 dịch vụ/sản phẩm'); return; }
        const missingVariant = comboItems.find((item) => !item.variant?.id);
        if (missingVariant) { reportError(`"${missingVariant.service.name}" chưa có biến thể`); return; }
        setSaving(true);
        try {
            await api.post('/services/combo', {
                name: form.name.trim(),
                typeService: 'combo',
                description: form.description,
                displayLocation: form.displayLocation,
                internalNote: form.internalNote,
                isWeb: form.isWeb,
                urlImg: form.urlImg,
                price: Math.round(comboTotal),
                code: 'COMBO',
                comboItems: comboItems.map((item) => ({
                    serviceId: item.service.id,
                    variantId: item.variant.id,
                    quantity: item.quantity,
                })),
                variants: comboItems.map((item) => ({
                    variantId: item.variant.id,
                    quantity: item.quantity,
                })),
            });
            setShowSuccess(true);
            setTimeout(() => navigate('/admin/services', { replace: true }), 1200);
        } catch (err) {
            reportError(err.response?.data?.message || 'Tạo combo thất bại');
        } finally {
            setSaving(false);
        }
    };

    // Check nếu service đã được thêm vào combo
    const isInCombo = (serviceId) => comboItems.some((item) => item.service.id === serviceId);

    if (loadingServices) {
        return (
            <div className={styles.createForm}>
                <div className={styles.formHeader}>
                    <div className={styles.formHeaderLeft}>
                        <div className={styles.skeleton} style={{ width: 40, height: 40, borderRadius: 10 }} />
                        <div className={styles.skeleton} style={{ width: 160, height: 28, borderRadius: 8 }} />
                    </div>
                </div>
                <div className={styles.comboLayout}>
                    <div className={styles.comboLayoutLeft}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}><div className={styles.skeleton} style={{ width: 140, height: 20, borderRadius: 6 }} /></div>
                            <div className={styles.cardBody}>
                                {[1, 2, 3].map((i) => (
                                    <div key={i} style={{ marginBottom: 20 }}>
                                        <div className={styles.skeleton} style={{ width: '30%', height: 16, borderRadius: 6, marginBottom: 8 }} />
                                        <div className={styles.skeleton} style={{ width: '100%', height: 44, borderRadius: 10 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.comboLayoutRight}>
                        <div className={styles.card} style={{ height: 400 }}>
                            <div className={styles.cardHeader}><div className={styles.skeleton} style={{ width: 200, height: 20, borderRadius: 6 }} /></div>
                            <div className={styles.cardBody}>
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, alignItems: 'center' }}>
                                        <div className={styles.skeleton} style={{ width: 40, height: 40, borderRadius: 8 }} />
                                        <div style={{ flex: 1 }}>
                                            <div className={styles.skeleton} style={{ width: '70%', height: 16, borderRadius: 6, marginBottom: 6 }} />
                                            <div className={styles.skeleton} style={{ width: '40%', height: 12, borderRadius: 6 }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.createForm}>
            {saving && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.spinner} />
                    Đang tạo combo...
                </div>
            )}

            {/* Header */}
            <div className={styles.formHeader}>
                <div className={styles.formHeaderLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <h1 className={styles.formTitle}>Tạo Combo</h1>
                </div>
                <button className={styles.submitBtn} onClick={handleSubmit} disabled={saving}>
                    <Save /> Lưu combo
                </button>
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            <div className={styles.comboLayout}>
                {/* ===== CỘT TRÁI: Form + Items đã chọn ===== */}
                <div className={styles.comboLayoutLeft}>
                    {/* Thông tin combo */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin combo</div>
                        <div className={styles.cardBody}>
                            <div className={styles.comboInfoGrid}>
                                {/* Cột trái: Tên + Giá + Ảnh */}
                                <div className={styles.comboInfoLeft}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Tên combo <span>*</span></label>
                                        <input
                                            className={styles.formInput}
                                            value={form.name}
                                            onChange={(e) => {
                                                updateForm('name', e.target.value);
                                                setNameEdited(true);
                                            }}
                                            placeholder="Tự động tạo từ dịch vụ đã chọn"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Giá combo</label>
                                        <KeypadInput
                                            className={styles.formInput}
                                            value={form.customPrice}
                                            onChange={(raw) => updateForm('customPrice', raw)}
                                            title="Giá combo"
                                            suffix="đ"
                                            placeholder={comboCalcTotal > 0 ? formatCurrency(comboCalcTotal) : 'Tự tính từ dịch vụ'}
                                        />
                                        {comboCalcTotal > 0 && form.customPrice === '' && (
                                            <span style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                                                Tự động: {formatCurrency(comboCalcTotal)}
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Ảnh combo</label>
                                        <div className={styles.imageUpload}>
                                            <div className={styles.imagePreview} onClick={() => fileRef.current?.click()}>
                                                {imgPreview || form.urlImg ? (
                                                    <img src={imgPreview || form.urlImg} alt="Preview" />
                                                ) : (
                                                    <div className={styles.imageUploadPlaceholder}>
                                                        <Upload />
                                                        <span>Chọn ảnh</span>
                                                    </div>
                                                )}
                                            </div>
                                            <input ref={fileRef} type="file" accept="image/*" className={styles.imageUploadInput} onChange={handleImageSelect} />
                                        </div>
                                    </div>
                                </div>
                                {/* Cột phải: Mô tả */}
                                <div className={styles.comboInfoRight}>
                                    <div className={styles.formGroup} style={{ height: '100%' }}>
                                        <label className={styles.formLabel}>Mô tả</label>
                                        <textarea
                                            className={styles.formTextarea}
                                            value={form.description}
                                            onChange={(e) => updateForm('description', e.target.value)}
                                            placeholder="Nhập mô tả combo"
                                            style={{ flex: 1, minHeight: 0 }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Items đã chọn */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            Trong combo ({comboItems.length})
                        </div>
                        <div className={styles.cardBody}>
                            {comboItems.length === 0 ? (
                                <div className={styles.comboEmpty}>
                                    <ShoppingBag size={40} />
                                    <p>Chưa có mục nào</p>
                                    <span>Chọn dịch vụ hoặc sản phẩm từ danh sách bên phải</span>
                                </div>
                            ) : (
                                <>
                                    {comboItems.map((item, idx) => (
                                        <div key={idx} className={styles.comboItem}>
                                            <img src={item.service.urlImg || DEFAULT_IMG} alt={item.service.name} className={styles.comboItemImg} />
                                            <div className={styles.comboItemInfo}>
                                                <div className={styles.comboItemName}>
                                                    {item.service.name}
                                                    {item.service.variants?.length > 1 && (
                                                        <span style={{ fontSize: 12, color: '#0098F6', marginLeft: 6 }}>
                                                            {getVariantLabel(item.variant)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className={styles.comboItemPrice}>
                                                    {formatCurrency(getItemPrice(item.variant))}
                                                    {' × '}
                                                    {item.quantity}
                                                    {' = '}
                                                    <strong>{formatCurrency(getItemPrice(item.variant) * item.quantity)}</strong>
                                                </div>
                                            </div>
                                            <div className={styles.comboItemQty}>
                                                <button className={styles.comboQtyBtn} onClick={() => updateQty(idx, -1)}><Minus size={14} /></button>
                                                <span className={styles.comboQtyValue}>{item.quantity}</span>
                                                <button className={styles.comboQtyBtn} onClick={() => updateQty(idx, 1)}><Plus size={14} /></button>
                                            </div>
                                            <button className={styles.comboItemRemove} onClick={() => removeItem(idx)}><X /></button>
                                        </div>
                                    ))}
                                    <div className={styles.comboTotal}>
                                        <span className={styles.comboTotalLabel}>Tổng giá combo</span>
                                        <span className={styles.comboTotalValue}>{formatCurrency(comboTotal)}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {/* Thông tin bổ sung */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>Thông tin bổ sung</div>
                        <div className={styles.cardBody}>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Vị trí trưng bày</label>
                                    <input
                                        className={styles.formInput}
                                        value={form.displayLocation}
                                        onChange={(e) => updateForm('displayLocation', e.target.value)}
                                        placeholder="VD: Kệ 2, Bàn 3"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Ghi chú nội bộ</label>
                                    <input
                                        className={styles.formInput}
                                        value={form.internalNote}
                                        onChange={(e) => updateForm('internalNote', e.target.value)}
                                        placeholder="Ghi chú cho nhân viên"
                                    />
                                </div>
                                <div className={`${styles.formGroup} ${styles.formGridFull}`}>
                                    <label className={styles.formToggle}>
                                        <input
                                            type="checkbox"
                                            checked={form.isWeb}
                                            onChange={(e) => updateForm('isWeb', e.target.checked)}
                                        />
                                        Hiển thị trên website
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ===== CỘT PHẢI: Catalog sidebar ===== */}
                <div className={styles.comboLayoutRight}>
                    <div className={styles.comboCatalog}>
                        <div className={styles.comboCatalogHeader}>
                            <h3 className={styles.comboCatalogTitle}>Chọn dịch vụ / sản phẩm</h3>
                        </div>

                        {/* Tabs */}
                        <div className={styles.comboCatalogTabs}>
                            {[
                                { value: 'all', label: 'Tất cả' },
                                { value: 'dichvu', label: 'Dịch vụ', icon: <Scissors size={14} /> },
                                { value: 'sanpham', label: 'Sản phẩm', icon: <Package size={14} /> },
                            ].map((tab) => (
                                <button
                                    key={tab.value}
                                    className={`${styles.comboCatalogTab}${catalogTab === tab.value ? ` ${styles.comboCatalogTabActive}` : ''}`}
                                    onClick={() => setCatalogTab(tab.value)}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className={styles.comboCatalogSearch}>
                            <Search size={16} />
                            <input
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Tìm kiếm..."
                            />
                            {searchText && (
                                <button onClick={() => setSearchText('')}><X size={14} /></button>
                            )}
                        </div>

                        {/* List */}
                        <div className={styles.comboCatalogList}>
                            {loadingServices ? (
                                <div className={styles.comboCatalogEmpty}>Đang tải...</div>
                            ) : filteredServices.length === 0 ? (
                                <div className={styles.comboCatalogEmpty}>Không tìm thấy</div>
                            ) : (
                                filteredServices.map((service) => {
                                    const variants = service.variants || [];
                                    if (variants.length === 0) return null;
                                    const added = isInCombo(service.id);
                                    const isPickerOpen = variantPicker?.service.id === service.id;

                                    return (
                                        <div key={service.id}>
                                            <div
                                                className={`${styles.comboCatalogItem}${added ? ` ${styles.comboCatalogItemAdded}` : ''}`}
                                                onClick={() => addItem(service)}
                                            >
                                                <img src={service.urlImg || DEFAULT_IMG} alt={service.name} className={styles.comboCatalogItemImg} />
                                                <div className={styles.comboCatalogItemInfo}>
                                                    <div className={styles.comboCatalogItemName}>{service.name}</div>
                                                    <div className={styles.comboCatalogItemPrice}>
                                                        {formatCurrency(getItemPrice(variants[0]))}
                                                        {variants.length > 1 && (
                                                            <span className={styles.comboCatalogVariantCount}>
                                                                {variants.length} loại
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <button className={styles.comboCatalogItemAdd}>
                                                    {added ? '✓' : variants.length > 1 ? <ChevronDown size={16} /> : <Plus size={16} />}
                                                </button>
                                            </div>

                                            {/* Variant picker inline */}
                                            {isPickerOpen && (
                                                <div className={styles.variantPickerList}>
                                                    <div className={styles.variantPickerTitle}>Chọn phân loại:</div>
                                                    {variants.map((v) => {
                                                        const vAdded = comboItems.some(
                                                            (item) => item.service.id === service.id && item.variant.id === v.id,
                                                        );
                                                        return (
                                                            <div
                                                                key={v.id}
                                                                className={`${styles.variantPickerItem}${vAdded ? ` ${styles.variantPickerItemAdded}` : ''}`}
                                                                onClick={(e) => { e.stopPropagation(); addItemWithVariant(service, v); }}
                                                            >
                                                                <span className={styles.variantPickerLabel}>
                                                                    {getVariantLabel(v)}
                                                                </span>
                                                                <span className={styles.variantPickerPrice}>
                                                                    {formatCurrency(getItemPrice(v))}
                                                                </span>
                                                                <span className={styles.variantPickerAdd}>
                                                                    {vAdded ? '✓' : <Plus size={14} />}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>Tạo combo thành công</span>
                    </div>
                </div>
            )}
        </div>
    );
}
